// The goal is to put all of my date functions in this file and import them as needed.
// THis will force me to always use a timezone adjusted time

import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import isoWeek from "dayjs/plugin/isoWeek.js";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import isBetween from "dayjs/plugin/isBetween.js";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import { dev } from "$app/environment";
import { config } from "$config";

//////////////////////////////////////////////////
// OVERRIDES IN CASE OF NO SHOW - CLOSES SUBMISSIONS
//////////////////////////////////////////////////
const NO_SHOW_OVERRIDE: boolean = config.NO_SHOW_THIS_WEEK;

// THURSDAY SHOW OVERRIDE - set to true for thursday show - dedicated to Gina K.
const THURSDAY_SHOW: boolean = false;

// day adjustment variables based on show day
const DAY_ADJUSTMENT: number = THURSDAY_SHOW ? 4 : 5;
const END_WINDOW_HOUR_SPAN: number = THURSDAY_SHOW ? 77 : 53;

//////////////////////////////////////////////////

// plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.extend(isBetween);
dayjs.extend(relativeTime);
dayjs.extend(isToday);

// week of
export const week_of = (): string => dayjs().format("dddd, MMMM Do");

// set default timezone used like .tz()
// parse timestamps from stripe
export const parse_stripe_date = (unix: number) =>
  dayjs.utc(dayjs.unix(unix)).utc().format("YYYY-MM-DD");

export const parse_stripe_start_date = (unix: number) =>
  dayjs.utc(dayjs.unix(unix)).subtract(1, "day").format("YYYY-MM-DD");

export const convert_date_joined = (date: string) =>
  dayjs.utc(date).toISOString();

export const parse_date_joined = (date: string) =>
  dayjs.utc(date).format("M/D/YY");

export const new_joined_date = () => dayjs.utc().toISOString();
// parse timestamps from stripe
export const parse_patreon_date_for_migration = (date: Date) =>
  dayjs.utc(date).format("YYYY-MM-DD");

// time until subscription expires
export const time_until_subscription_expires = (date: string) =>
  dayjs.utc(date).fromNow();

// nice internationally sound date format for pledge import
export const pledge_import_date = (date: Date | string) =>
  dayjs.utc(date).format("MMMM Do YYYY");

// nice internationally sound date format for pledge import
export const email_date = (date: Date | string) =>
  dayjs.tz(date).format("MMMM Do YYYY");

export const email_date_and_time = (date: Date | string) =>
  dayjs.tz(date).format("MMMM Do YYYY, h:mm A");

// i need to figure out if today is same day as the pledge date in YYYY-MM-DD format
export const is_grace_period = (date: string) => dayjs().isSame(date, "day");

// we need to parse our dates to UTC
export const utc_parse_date = (date: string) => dayjs.utc(date);

// for countdown emails
export const countdown_show_start = dayjs
  .utc()
  .tz("America/Chicago")
  .isoWeekday(+5)
  .hour(18)
  .minute(10);
export const countdown_show_start_test = countdown_show_start.format(
  "dddd h:mma z - M/DD/YY",
);

const show_schedule_format = "dddd h:mma z";

export const show_schedule = [
  {
    key: "Show setup (with guest) starts",
    time: dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(17)
      .minute(45)
      .second(0)
      .tz(dayjs.tz.guess()) // guess the timezone of the viewer
      .format(show_schedule_format),
  },
  {
    key: "Stream goes live -> slideshow starts",
    time: dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(18)
      .minute(10)
      .second(0)
      .tz(dayjs.tz.guess()) // guess the timezone of the viewer
      .format(show_schedule_format),
  },
  {
    key: "Show starts -> intro",
    time: dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(18)
      .minute(30)
      .second(0)
      .tz(dayjs.tz.guess()) // guess the timezone of the viewer
      .format(show_schedule_format),
  },
  {
    key: "Guest introduction -> interview",
    time: dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(18)
      .minute(38)
      .second(0)
      .tz(dayjs.tz.guess()) // guess the timezone of the viewer
      .format(show_schedule_format),
  },
  {
    key: "Crafting segment (est. start)",
    time: dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(19)
      .minute(10)
      .second(0)
      .tz(dayjs.tz.guess()) // guess the timezone of the viewer
      .format(show_schedule_format),
  },
  {
    key: "Show ending segments (est.)",
    time: dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(20)
      .minute(15)
      .second(0)
      .tz(dayjs.tz.guess()) // guess the timezone of the viewer
      .format(show_schedule_format),
  },
];

// setting up times in GMT so I can localize them later
const utcShowStart = dayjs
  .utc()
  .tz("America/Chicago")
  .isoWeekday(+DAY_ADJUSTMENT)
  .hour(18)
  .minute(30)
  .second(0);

// set default timezone used like .tz()
dayjs.tz.setDefault("America/Chicago");

// figure out the UTC offset
export const UTC_offset = dayjs().utcOffset() / 60;

// take a stab the user's timezone
const guess = dayjs.tz.guess();

// localized start time of show
export const localizedStartDay = utcShowStart.tz(guess).format("dddd");
export const localizedStartTime = utcShowStart.tz(guess).format("h:mma z");

// figure out if the hour of the day is night, afternoon, or morning
const hourInt = parseInt(utcShowStart.tz(guess).format("HH"));
export const localizedStartHuman =
  hourInt >= 17 ? "Night" : hourInt >= 12 ? "Afternoon" : "Morning";

// set a base now object
export const now = dayjs();
export const nowUTC = dayjs.utc();
export const nowCentral = dayjs.utc().tz();
export const utc_now = () => nowUTC;

export const time_from_now = (day: string) => dayjs.tz(day).tz(guess).fromNow();
export const utc_time_from_now = (day: string) =>
  dayjs.utc(day).tz(guess).fromNow();
export const dayjs_utc_time_from_now = (day: Dayjs) =>
  dayjs.utc(day).tz(guess).fromNow();
export const dayjs_time_from_now = (day: Dayjs) =>
  dayjs.tz(day).tz(guess).fromNow();
export const comment_time_from_now = (day: string) =>
  dayjs(day).tz(guess).fromNow();
export const bonus_from = (day: string) => dayjs(day).fromNow();
export const video_time_from_now = (day: string, created_on: string) => {
  if (dayjs(day).isToday()) {
    return dayjs(created_on).fromNow();
  }
  return dayjs.tz(day).tz(guess).fromNow();
};

// current month number for card exchange birthdays
export const current_month_number = dayjs.utc().tz(guess).month() + 1;

// function to take two dates, make sure they are in UTC, and then figure out if the current UTC time is between them
// if end is not defined, it will default to now
export const is_between_utc = (start: string, end?: string) => {
  const utc_end = end ? dayjs.utc(end) : dayjs.utc();

  // I am giving the start time 1 extra day just to make sure people who sign up
  // aren't immediately denied entry to the site
  const nice_start = dayjs(dayjs.utc(start).subtract(1, "day"));
  return dayjs.utc().isBetween(nice_start, utc_end, "day", "[]");
};

// zoom call display
export const zoom_month = (date: string) =>
  dayjs.tz(date).tz(guess).format("MMM");
export const zoom_day = (date: string) => dayjs.tz(date).tz(guess).format("D");
export const zoom_date_detailed = (date: string) =>
  dayjs.tz(date).tz(guess).format("dddd h:mma z");
export const video_date_detailed = (date: string) =>
  dayjs.tz(date).tz(guess).format("M/DD/YY");

export const pledge_start = (date: Date) =>
  dayjs.tz(date).tz(guess).format("M/DD/YY");

// receives a UTC datetime from directus (created by directus) and returns a formatted date
// in the local time zone of the viewer
export const directus_date = (date: string) =>
  dayjs.utc(date).local().format("MMMM DD, YYYY | h:mma z");

export const blog_update_date_no_time = (date: string) =>
  dayjs.utc(date).local().format("MMMM DD, YYYY");

// receives a UTC datetime from directus (created by directus) and returns a formatted date
// in the local time zone of the viewer
export const directus_date_condensed = (date: string) =>
  dayjs.utc(date).local().format("MMM DD, 'YY | h:mma z");

export const message_pill_date = (date: string) =>
  dayjs.utc(date).local().format("MMM Do");
// used for the news letter episode query
export const nextMonth = [
  dayjs().format("YYYY-MM-DD"),
  dayjs().add(1, "month").format("YYYY-MM-DD"),
];

// used for debugging
export const nowFormatted = now.format("dddd h:mma z - M/DD/YY");
export const nowUTCFormatted = nowUTC.format("dddd h:mma - M/DD/YY");
export const nowCentralFormatted = dayjs
  .utc()
  .tz()
  .format("dddd h:mma - M/DD/YY");

// one week from now (used in submit process video form)
export const oneWeek = dayjs().add(1, "week");

// compare the supplied event date against the current time in CT and return a true/false
export const isFuture = (event: string) => {
  return dayjs(event).tz().isAfter(nowCentral);
};

// compare the supplied event date against the current time in CT and return a true/false
export const give_away_cutoff = (event: string) => {
  return dayjs(event).add(5, "hour").tz().isAfter(nowCentral);
};

export const is_special_event_giveaway_open = (date: string): boolean => {
  if (dev) return true;
  if (!date) return false;
  const event_date = dayjs(date);
  const cutoff = event_date.add(2, "day");
  return now.isBetween(event_date, cutoff, "day", "[]");
};

export const is_future_event = (event: string) =>
  dayjs().isBefore(event, "day");

// compare the supplied event date against the current time in CT and return a true/false
export const hasHappened = (event: string) => {
  return dayjs(event).tz().isBefore(nowCentral);
};

// a standard format for episode date display
// I DONT SAVE SHOW DATES WITH TIMES, ONLY CALENDAR DATES
export const episodeDate = (date: string | Dayjs) => {
  return dayjs.tz(date).hour(18).minute(30).local().format("MMMM Do, YYYY");
};

// a formatter and time zoner for the event date/time
export const event_date = (date: string) => {
  return dayjs.tz(date).local().format("MMMM Do, YYYY @ hh:mma z (Z)");
};

// a formatter and time zoner for the event date/time
export const event_date_central = (date: string) => {
  return dayjs.tz(date).format("MMMM Do, YYYY @ hh:mma z");
};

export const event_diff = (date: string) => {
  dayjs.tz(date).local().valueOf() - dayjs().valueOf();
  return dayjs.tz(date).local().valueOf() - dayjs().valueOf();
};

// a formatter and time zoner for the event date/time
export const event_schedule_date = (date: string) => {
  return dayjs.tz(date).local().format("h:mma z");
};

export const event_schedule_tz = (date: string) => {
  return dayjs.tz(date).local().format("z (Z)");
};

// a formatter and time zoner for the event date/time
export const event_cal_link = (date: string) => {
  return {
    date: dayjs(date).format("YYYY-MM-DD"),
    time: dayjs(date).format("HH:mm"),
    end: dayjs(date).add(3, "hours").format("HH:mm"),
  };
};

export const event_date_no_format = (date: string) => {
  return dayjs.tz(date, "America/Chicago").local();
};

export const is_date_between = (s?: string, e?: string): boolean => {
  // gaurd
  if (!s || !e) return false;

  return dayjs().isBetween(s, e, "day", "[]");
};

export const emailDate = (date: string) => {
  return dayjs(date).format("dddd - MMMM Do");
};

export const gallery_date = (date: string) => {
  return dayjs(date).format("M/D/YY");
};

export const UTC_check = dayjs.utc().isoWeekday(0).hour(0).minute(0).second(0);

export const dayOfWeekNumberChecker = utcShowStart
  .tz()
  .format("dddd d h:mma z");

export const isUTC = (time: Dayjs) => {
  return time.isUTC();
};

const now_central = dayjs().utc().tz("America/Chicago");

export const convert_to_central = (date: string) =>
  dayjs(date).utc().tz("America/Chicago");

export const lastShow = () => {
  //if the current time (relative to day of week) is before friday, get last week's friday's date
  if (
    dayjs(now_central).isBefore(
      dayjs
        .utc()
        .tz("America/Chicago")
        .isoWeekday(+DAY_ADJUSTMENT)
        .hour(0)
        .minute(0),
    )
  ) {
    return episodeDate(
      dayjs.utc().tz("America/Chicago").isoWeekday(-2).hour(18).minute(30),
    );
  } else {
    return episodeDate(
      dayjs
        .utc()
        .tz("America/Chicago")
        .isoWeekday(+DAY_ADJUSTMENT)
        .hour(18)
        .minute(30),
    );
  }
};

export const lastShowUTC = () => {
  //if the current time (relative to day of week) is before friday, get last week's friday's date
  if (
    dayjs(now_central).isBefore(
      dayjs
        .utc()
        .tz("America/Chicago")
        .isoWeekday(+DAY_ADJUSTMENT)
        .hour(0)
        .minute(0),
    )
  ) {
    return dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(-2)
      .hour(18)
      .minute(30)
      .second(0);
  } else {
    return dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(18)
      .minute(30)
      .second(0);
  }
};

// Setup default times and dates for submission window activities
// adding formats to the start and end dates breaks the isBetween function
export const startWindow = lastShowUTC(); // Friday night at 6:00pm CT
export const endWindow = lastShowUTC()
  .add(END_WINDOW_HOUR_SPAN, "hour")
  .add(29, "minute"); // Sunday night at 11:59pm CT
export const acceptingSubmissions: boolean = NO_SHOW_OVERRIDE
  ? false
  : dayjs(now_central).isBetween(startWindow, endWindow, "minute", "[]"); // [] == inclusive
export const isLiveNow: boolean = NO_SHOW_OVERRIDE
  ? false
  : dayjs(now_central).isBetween(
    startWindow,
    lastShowUTC().add(2, "hour").add(30, "minute"),
    "minute",
    "[]",
  ); // [] == inclusive

// export const endWindowFormatted = episodeDate(endWindow)

// setup late submission window
export const startPatreonWindow = dayjs
  .utc()
  .tz("America/Chicago")
  .isoWeekday(+1)
  .hour(0)
  .minute(0); // CHANGE THIS BACK TO +1 days                        // Monday at 12:00 pm CT
export const endPatreonWindow = dayjs
  .utc()
  .tz("America/Chicago")
  .isoWeekday(+DAY_ADJUSTMENT)
  .hour(0)
  .minute(0)
  .second(0); // Friday at 0:00 am CT
export const acceptingLateSubmissions = dayjs(now_central).isBetween(
  startPatreonWindow,
  endPatreonWindow,
  "minute",
  "[]",
); // [] == inclusive

// exports to check time values
export const showStart = utcShowStart.tz().format("dddd h:mma z");
export const startWindowFormatted = startWindow
  .tz(guess)
  .format("dddd @ h:mma z");
export const endWindowFormatted = endWindow.tz(guess).format("dddd @ h:mma z");
export const patreonStartWindowFormatted = startPatreonWindow
  .tz(guess)
  .format("dddd @ h:mma z");
export const patreonEndWindowFormatted = endPatreonWindow
  .tz(guess)
  .format("dddd @ h:mma z");

const startInRevue = dayjs
  .utc()
  .tz("America/Chicago")
  .isoWeekday(+3)
  .hour(18)
  .minute(0);

//////////////////////////////////////////////////
//  !Use this to reschedule the show!
//////////////////////////////////////////////////

// export const startWindow = lastShowUTC().add(24, 'hour')// REMOVE THE ADD 24 HOURS THING
// export const endWindow   = lastShowUTC().add(53 + 48, 'hour').add(29, 'minute')// REMOVE THE +24                                  // Sunday night at 11:59pm CT

type TimeObj = {
  day: string;
  time: string;
  zone: string;
};
// for the episode archive widget
export const archive_schedule = (): {
  zone: string;
  show_start: TimeObj;
  patreon_window_start: TimeObj;
  in_revue: TimeObj;
  patreon_window_end: TimeObj;
} => {
  // formatters
  const day = (day: dayjs.Dayjs) => day.tz(guess).format("dddd");
  const time = (time: dayjs.Dayjs) => time.tz(guess).format("h:mma");
  const zone = (time: dayjs.Dayjs) => time.tz(guess).format("z");

  return {
    zone: now.format("z"),
    show_start: {
      day: day(utcShowStart),
      time: time(utcShowStart),
      zone: zone(utcShowStart),
    },
    patreon_window_start: {
      day: day(startPatreonWindow),
      time: time(startPatreonWindow),
      zone: zone(startPatreonWindow),
    },
    in_revue: {
      day: day(startInRevue),
      time: time(startInRevue),
      zone: zone(startPatreonWindow),
    },
    patreon_window_end: {
      day: day(endPatreonWindow.minute(-1)),
      time: time(endPatreonWindow.minute(-1)),
      zone: zone(endPatreonWindow.minute(-1)),
    },
  };
};

// we need this for stupid reasons
export const submission_window_in_ms = (): number => {
  const public_window = endWindow.diff(startWindow);
  const patreon_window = endPatreonWindow.diff(startPatreonWindow);

  return public_window + patreon_window;
};

export const ms_since_last_show = () => {
  // figure out when the last show was (generally accepted)
  //if the current time (relative to day of week) is before friday, get last week's friday's date
  if (
    dayjs(now_central).isBefore(
      dayjs()
        .utc()
        .tz("America/Chicago")
        .isoWeekday(+DAY_ADJUSTMENT)
        .hour(0)
        .minute(0),
    )
  ) {
    // acceptingSubmissions
    const last_show = dayjs
      .utc()
      .tz("America/Chicago")
      .isoWeekday(-2)
      .hour(18)
      .minute(30)
      .second(0);
    if (dev)
      console.log("last_show", last_show.format("MM/DD/YY dddd h:mma z (Z)"));
    return Math.abs(dayjs().diff(last_show));
  } else {
    // acceptingLateSubmissions
    const last_show = dayjs()
      .utc()
      .tz("America/Chicago")
      .isoWeekday(+DAY_ADJUSTMENT)
      .hour(18)
      .minute(30)
      .second(0);

    if (dev)
      console.log("last_show", last_show.format("MM/DD/YY dddd h:mma z (Z)"));
    return Math.abs(dayjs().diff(last_show));
  }
};
